import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import chefVideo from '../assets/chec.mp4'; // Добавьте соответствующее видео

// Основной контейнер
const ChefContainer = styled(motion.div)`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  overflow: hidden;
  background-color: #000;
`;

// Видео контейнер с фиксированными пропорциями
const VideoBackground = styled(motion.div)`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 60%;
  min-height: 80vh; /* Обеспечивает минимальную высоту */
  overflow: hidden;
  z-index: -2;
  display: flex;
  justify-content: center;
  align-items: flex-end;
`;

// Стиль для самого видео с соотношением сторон 4:5
const BackgroundVideo = styled.video`
  width: auto;
  height: 100%;
  aspect-ratio: 4/5; /* Фиксированное соотношение сторон 4:5 */
  object-fit: cover;
  object-position: center bottom;
`;

// Тёмный оверлей поверх видео
const Overlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  z-index: -1;
`;

// Контейнер для текста с атмосферным размытием
const TextContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 500px;
  position: absolute;
  top: 10%;
  z-index: 10;
  padding: 1.5rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
    width: 90%;
  }
`;

// Стилизованный заголовок
const Title = styled(motion.h1)`
  font-size: 3.2rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 1rem;
  position: relative;
  font-family: 'Black Han Sans', sans-serif;
  font-weight: 700;
  letter-spacing: -0.5px;
  line-height: 1.1;
  color: white;
  
  .gradient-text {
    background: linear-gradient(
      120deg,
      #ffffff 0%,
      #ff6ad5 25%,
      #c659ff 50%,
      #ff36be 75%,
      #ffffff 100%
    );
    background-size: 400% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: gradientFlow 6s ease infinite;
  }
  
  @keyframes gradientFlow {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

// Подзаголовок с улучшенной видимостью
const Subtitle = styled(motion.p)`
  font-size: 1.4rem;
  color: #FFFFFF;
  opacity: 1; // Увеличиваем непрозрачность до 100%
  text-align: center;
  margin-bottom: 2rem;
  font-weight: 500; // Усиливаем вес шрифта
  line-height: 1.4;
  max-width: 100%;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.3); // Добавляем лёгкое свечение
  letter-spacing: 0.5px; // Улучшаем читаемость через межбуквенное расстояние
  
  .gradient-text {
    background: linear-gradient(
      120deg,
      #ffffff 0%,
      #ff6ad5 25%,
      #c659ff 50%,
      #ff36be 75%,
      #ffffff 100%
    );
    background-size: 400% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: gradientFlow 6s ease infinite;
    font-weight: 700; // Делаем градиентный текст ещё жирнее
    text-shadow: none; // Убираем тень для градиентного текста
  }
  
  @media (max-width: 768px) {
    font-size: 1.2rem; // Немного увеличиваем размер шрифта на мобильных устройствах
    margin-bottom: 1.5rem;
  }
`;

// Упрощенная кнопка без пульсирующих теней, чтобы избежать блика
const TryButton = styled(motion.button)`
  padding: 1.2rem 0;
  background: linear-gradient(to right, #ff36be, #ff6ad5);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  overflow: hidden;
  position: fixed;
  bottom: 5%;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  width: 80%;
  max-width: 400px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  letter-spacing: 1px;
  z-index: 100;
  
  /* Статическое свечение без анимации */
  box-shadow: 
    0 0 10px #ff36be,
    0 0 20px rgba(255, 106, 213, 0.6);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 
      0 0 15px #ff36be,
      0 0 25px #ff6ad5;
  }
  
  &:active {
    transform: translateY(1px);
  }
`;

// Иконка стрелки внутри кнопки
const ArrowIcon = styled(motion.span)`
  margin-left: 8px;
  display: inline-block;
  font-size: 1.1rem;
  
  ${TryButton}:hover & {
    animation: pulse 1.5s infinite;
  }
  
  @keyframes pulse {
    0% {
      transform: translateX(0);
    }
    50% {
      transform: translateX(5px);
    }
    100% {
      transform: translateX(0);
    }
  }
`;

// Компонент ChefBuddy
const ChefBuddy = () => {
  const navigate = useNavigate();
  const [isExiting, setIsExiting] = useState(false);
  const [videoStarted, setVideoStarted] = useState(false);
  const videoRef = useRef(null);

  // Состояние для контроля появления компонентов
  const [textVisible, setTextVisible] = useState(false);
  const [buttonVisible, setButtonVisible] = useState(false);
  
  // Обработчик начала воспроизведения видео
  const handleVideoStart = () => {
    setVideoStarted(true);
  };

  // Эффект для анимированного появления компонентов
  useEffect(() => {
    // Сначала показываем текст
    setTimeout(() => {
      setTextVisible(true);
      
      // После показа текста, включаем видео
      setTimeout(() => {
        setVideoStarted(true);
        
        // После видео показываем кнопку
        setTimeout(() => {
          setButtonVisible(true);
        }, 1000);
      }, 1200);
    }, 500);
  }, []);

  // Эффект для автоматического воспроизведения видео с интервалом
  useEffect(() => {
    if (videoRef.current && videoStarted) {
      // Установка громкости на ноль для бесшумного воспроизведения
      videoRef.current.muted = true;
      
      // Замедление скорости воспроизведения видео
      videoRef.current.playbackRate = 0.7;
      
      // Функция для перезапуска видео
      const restartVideo = () => {
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(error => {
          console.error("Воспроизведение было предотвращено:", error);
        });
      };
      
      // Обработчик окончания видео
      const handleVideoEnd = () => {
        // Пауза 1 секунда перед перезапуском
        setTimeout(() => {
          restartVideo();
        }, 1000);
      };
      
      // Добавление обработчика события окончания видео
      videoRef.current.addEventListener('ended', handleVideoEnd);
      
      // Запуск видео
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.error("Autoplay was prevented:", error);
        });
      }
      
      // Удаление обработчика при размонтировании
      return () => {
        if (videoRef.current) {
          videoRef.current.removeEventListener('ended', handleVideoEnd);
        }
      };
    }
  }, [videoStarted]);

  // Эффект для анимированного перехода
  const handleContinue = () => {
    setIsExiting(true);
    setTimeout(() => {
      navigate('/food');
    }, 500);
  };

  return (
    <AnimatePresence>
      {!isExiting ? (
        <ChefContainer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.5 } }}
        >
          {/* Фоновое видео - без inline стилей */}
          <VideoBackground
            initial={{ opacity: 0 }}
            animate={{ opacity: videoStarted ? 1 : 0 }}
            transition={{ duration: 1 }}
          >
            <BackgroundVideo
              ref={videoRef}
              playsInline
              muted
              loop={false}
              autoPlay={videoStarted}
            >
              <source src={chefVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </BackgroundVideo>
          </VideoBackground>
          
          {/* Тёмный оверлей */}
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            transition={{ duration: 1 }}
          />

          {/* Контейнер с текстом */}
          <TextContainer
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: textVisible ? 1 : 0, y: textVisible ? 0 : 20 }}
            transition={{ duration: 0.8 }}
          >
            <Title
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: textVisible ? 0 : -20, opacity: textVisible ? 1 : 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <span className="gradient-text">Don't</span> <span className="white-text">worry.</span>
            </Title>
            
            <Subtitle
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: textVisible ? 0 : 20, opacity: textVisible ? 1 : 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              Your personal <span className="gradient-text">AI Buddy</span>  knows
            </Subtitle>
          </TextContainer>
          
          {/* Упрощенная анимация кнопки - только появление без дополнительных эффектов */}
          <TryButton
            initial={{ opacity: 0 }}
            animate={{ opacity: buttonVisible ? 1 : 0 }}
            transition={{ duration: 0.7 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleContinue}
          >
            Let's Try
            <ArrowIcon>→</ArrowIcon>
          </TryButton>
        </ChefContainer>
      ) : null}
    </AnimatePresence>
  );
};

export default ChefBuddy; 